import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboard } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { fmtTime, mealName, passChip, serveDateText, windowText } from '../lib/format';
import { ResidentBar } from '../components/resident';
import { Chip, QrCanvas, Spinner } from '../components/ui';
import { MealShareButtons, passQrValue, passVenueText, SeatList, SeatsView, ServedHistory, type PassScope } from '../components/passes';

export default function PassPage() {
  const { sittingId = '' } = useParams();
  const dash = useDashboard();
  const qc = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const pass = dash.data?.passes.find((p) => p.sitting.id === sittingId);
  const flat = dash.data?.flat;

  if (!pass) {
    return (
      <div className="shell">
        <ResidentBar back={{ to: '/', label: 'My passes' }} />
        <div className="app-body">{dash.isPending ? <div className="empty"><Spinner /></div> : <p className="err">{dash.isError ? errorText(dash.error) : 'No pass for this meal.'}</p>}</div>
      </div>
    );
  }

  const s = pass.sitting;
  const locked = pass.state === 'locked';
  const chip = passChip(pass);

  const scope: PassScope | null = flat?.pass_token ? {
    flat: { label: flat.label, name: flat.contact_name, passToken: flat.pass_token },
    reissueHint: 'issue new QR codes under Menu → My family',
    onChanged: () => void qc.invalidateQueries({ queryKey: ['dashboard'] }),
  } : null;

  return (
    <div className="shell">
      <div className="app-bar slim">
        <Link className="link-btn" to="/">‹ My passes</Link>
        <Chip tone={chip.tone}>{chip.label}</Chip>
      </div>
      <main className="app-body" style={{ gap: 16 }}>
        <div className="pass-head">
          <p className="app-eyebrow">{s.day_label_bn} · {s.day_label} · {serveDateText(s)}</p>
          <h1 className="pass-title">{mealName(s)}</h1>
          <p className="app-sub">{windowText(s)}{passVenueText(pass) ? <> · <Link to="/venues">{passVenueText(pass)}</Link></> : null}</p>
        </div>

        <div className={`qr-card${locked && !reveal ? ' is-locked' : ''}`}>
          <QrCanvas value={passQrValue(pass)} />
          {locked && !reveal && (
            <div className="qr-lock">
              <b>Locked</b>
              <span>Unlocks when {pass.blocking_order?.code ?? 'your payment'} is confirmed by the Food Committee.</span>
            </div>
          )}
          <p className="qr-name">{pass.holder.name} · Flat {pass.holder.label}</p>
          <p className="qr-id mono">Pass {pass.id.slice(0, 4)}-{pass.id.slice(4, 8)}-{pass.id.slice(8, 12)}</p>
        </div>
        {locked && (
          <button className="link-btn" style={{ justifySelf: 'center' }} onClick={() => setReveal((v) => !v)}>
            {reveal ? 'Hide QR' : 'Show QR for the help desk'}
          </button>
        )}
        {pass.state === 'expired' && <div className="notice">This meal closed at {fmtTime(s.closes_at)}. Unused seats can no longer be served.</div>}

        <SeatsView pass={pass} />
        <ServedHistory pass={pass} />

        <p className="fine">Turn your screen brightness up at the counter. One QR covers everyone in your family for this meal: family can show the same QR, and the counter only serves the seats that are left. Your family pass on My passes works here too.</p>
        {!locked && scope && <MealShareButtons pass={pass} scope={scope} />}
        {scope && <SeatList pass={pass} scope={scope} />}
      </main>
    </div>
  );
}
